<script setup lang="ts">
import 'survey-core/defaultV2.min.css';
import { Model } from 'survey-core';
import { FlatLight } from 'survey-core/themes';
import { SurveyComponent } from 'survey-vue3-ui';
import { loadSurveyFromApi } from '../services/data/survey';
import { ref, onMounted, computed } from 'vue';
import { loadMultiRecommendations, loadQuestionsFromDelivery, loadReferences } from '../services/data/recommendations';

const survey = ref<any>(null);
const surveyJson = ref<any>(null);
const multiRecommendations = ref<any[]>([]);
const allQuestions = ref<any[]>([]);
const questionLevelRecommendations = ref<any>({});
const references = ref<any[]>([]);

const initializeSurveyModel = (surveyModel: Model) => {
  surveyModel.applyTheme(FlatLight);
  surveyModel.showCompletedPage = false;
  surveyModel.showTitle = false;
  surveyModel.checkErrorsMode = "onNextPage";
  surveyModel.requiredText = "";
  surveyModel.showNavigationButtons = true;
  surveyModel.showPrevButton = true;
  surveyModel.showQuestionNumbers = true;

  surveyModel.pages.forEach(page => {
    page.questions.forEach(question => {
      question.isRequired = true;
      question.requiredErrorText = "Please provide an answer before continuing";
    });
  });

  surveyModel.onValueChanging.add((sender, options) => {
    const question = options.question;
    
    if (!question || question.getType() !== 'checkbox') return;
    
    const oldValue = options.oldValue;
    const newValue = options.value;
    
    // Check if this question has exclusive choices
    if (!question.exclusiveChoices || !Array.isArray(question.exclusiveChoices) || question.exclusiveChoices.length === 0) {
      return;
    }
    
    const exclusiveChoices = question.exclusiveChoices;
    
    const hasExclusiveValue = (valueArray) => {
      return Array.isArray(valueArray) && valueArray.some(v => exclusiveChoices.includes(v));
    };
    
    const hasNonExclusiveValue = (valueArray) => {
      return Array.isArray(valueArray) && valueArray.some(v => !exclusiveChoices.includes(v));
    };
    
    const oldHasExclusive = hasExclusiveValue(oldValue);
    const newHasExclusive = hasExclusiveValue(newValue);
    
    if (!oldHasExclusive && !newHasExclusive) return;
    
    // If exclusive choice was selected and there are other values, remove non-exclusive values
    if (!oldHasExclusive && newHasExclusive) {
      const filteredValue = newValue.filter(v => exclusiveChoices.includes(v));
      options.value = filteredValue;
      return;
    }
    
    // If exclusive choice was deselected and there are non-exclusive values, remove exclusive values
    if (oldHasExclusive && newHasExclusive && hasNonExclusiveValue(newValue)) {
      const filteredValue = newValue.filter(v => !exclusiveChoices.includes(v));
      options.value = filteredValue;
      return;
    }
  });

  surveyModel.onAfterRenderQuestion.add((sender, options) => {
    const question = options.question;
    const choices = question.choices;
    if (choices && choices.length > 0) {
      const stringViewers = options.htmlElement.querySelectorAll('.sd-item__control-label');
      let tooltipContainer = document.getElementById('custom-tooltip');
      if (!tooltipContainer) {
        tooltipContainer = document.createElement('div');
        tooltipContainer.id = 'custom-tooltip';
        document.body.appendChild(tooltipContainer);
      }
      let hasTooltip = false;
      stringViewers.forEach((element, index) => {
        const choice = choices[index];
        if (choice) {
          const tooltip = choice.jsonObj?.tooltip;
          if (tooltip) {
            hasTooltip = true;
            (element as HTMLElement).style.cursor = 'help';
            const icon = document.createElement('span');
            icon.className = 'tooltip-icon';
            icon.innerHTML = `
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style="display:inline-block;vertical-align:middle;">
                <circle cx="10" cy="10" r="9" stroke="#1976d2" stroke-width="2" fill="white"/>
                <text x="10" y="15" text-anchor="middle" font-size="13" font-family="Arial, sans-serif" fill="#1976d2">?</text>
              </svg>
            `;
            element.appendChild(icon);
            // Add mouse events for tooltip
            element.addEventListener('mouseenter', (e) => {
              tooltipContainer.innerHTML = tooltip;
              tooltipContainer.style.display = 'block';
              // Position the tooltip
              const rect = element.getBoundingClientRect();
              tooltipContainer.style.left = `${rect.right + 10}px`;
              tooltipContainer.style.top = `${rect.top}px`;
            });
            element.addEventListener('mouseleave', () => {
              tooltipContainer.style.display = 'none';
            });
          }
        }
      });
      if (hasTooltip) {
        const tip = document.createElement('div');
        tip.className = 'hover-tip';
        tip.innerHTML = `
          <span style="display:inline-flex;align-items:center;gap:6px;">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style="display:inline-block;vertical-align:middle;">
              <circle cx="10" cy="10" r="9" stroke="#1976d2" stroke-width="2" fill="white"/>
              <text x="10" y="15" text-anchor="middle" font-size="13" font-family="Arial, sans-serif" fill="#1976d2">?</text>
            </svg>
            Hover over the answers for more information
          </span>
        `;
        const titleElement = options.htmlElement.querySelector('.sd-question__title');
        if (titleElement && titleElement.nextSibling && titleElement.parentNode) {
          titleElement.parentNode.insertBefore(tip, titleElement.nextSibling);
        }
      }
    }
  });

  surveyModel.onComplete.add((sender, options) => {
    const results = sender.data;
    const surveyResults: any[] = [];
    const questionRecommendations: any[] = [];
    const multiRecsToShow: any[] = [];

    const extractKeyFromPath = (pathOrKey) => {
      if (typeof pathOrKey === 'string' && pathOrKey.startsWith('/')) {
        const parts = pathOrKey.split('/');
        return parts[parts.length - 1];
      }
      return pathOrKey;
    };

    // gather all the answers for each question
    const questionAnswers = {};
    Object.entries(results).forEach(([questionName, answer]) => {
      if (Array.isArray(answer)) {
        questionAnswers[questionName] = answer;
      } else {
        questionAnswers[questionName] = [answer];
      }
    });
    const allAnsweredKeys = Object.values(results).flat();

    // match multi recommendations to the questions answered
    multiRecommendations.value.forEach(rec => {
      if (Array.isArray(rec.tags)) {
        const recTagKeys = rec.tags.map(tag => extractKeyFromPath(tag));
        const matchedTags = recTagKeys.filter(tagKey => allAnsweredKeys.includes(tagKey));
        if (rec.type === 'or') {
          if (rec.minimum && matchedTags.length < rec.minimum) return;
          if (matchedTags.length > 0) {
            multiRecsToShow.push(rec);
          }
        } else { // AND logic
          const allTagsPresent = recTagKeys.every(tagKey => allAnsweredKeys.includes(tagKey));
          if (allTagsPresent) {
            multiRecsToShow.push(rec);
          }
        }
      }
    });

    const hideSingleTags = new Set();
    multiRecsToShow.forEach(rec => {
      if (rec.hideSingle && Array.isArray(rec.tags)) {
        rec.tags.forEach(tag => {
          const tagKey = extractKeyFromPath(tag);
          hideSingleTags.add(tagKey);
        });
      }
    });

    const questionOrder: any[] = [];
    Object.entries(results).forEach(([key, value], index) => {
      questionOrder.push({ index, key, value });
      let question;
      sender.pages.forEach(page => {
        const foundQuestion = page.questions.find(q => q.name === key);
        if (foundQuestion) {
          question = foundQuestion;
        }
      });
      if (question) {
        let formattedAnswer = Array.isArray(value) ? value.join(', ') : value;
        const questionData = {
          name: question.name,
          shortTitle: question.jsonObj.shortTitle || question.title,
          options: question.choices ? question.choices.map(c => c.text || c).join(', ') : '',
          answer: formattedAnswer
        };
        surveyResults.push(questionData);

        if (Array.isArray(value)) {
          value.forEach(val => {
            // skip if this tag should be hidden due to a multi-recommendation
            if (hideSingleTags.has(val)) {
              return;
            }
            const answerNode = allQuestions.value.find(q => q.key === val);
            let recommendation = null;
            let recommendationSection = null;
            let references = null;
            let image = null;
            let imageCaption = null;
            let showOnRight = false;

            if (answerNode && answerNode.recommendation) {
              // Use answer-level recommendation if available
              recommendation = answerNode.recommendation;
              recommendationSection = answerNode.recommendationSection || questionLevelRecommendations.value[key]?.recommendationSection;
              references = answerNode.references;
              image = answerNode.image;
              imageCaption = answerNode.imageCaption;
              showOnRight = answerNode.showOnRight;
            } else if (questionLevelRecommendations.value[key]) {
              // Fall back to question-level recommendation
              recommendation = questionLevelRecommendations.value[key].recommendation;
              recommendationSection = questionLevelRecommendations.value[key].recommendationSection;
            }

            if (recommendation) {
              const recObj = {
                question: questionData.shortTitle,
                recommendation: recommendation,
                recommendationSection: recommendationSection,
                references: references,
                answerKey: key,
                questionName: question.name,
                selectedValue: val,
                image: image,
                imageCaption: imageCaption,
                showOnRight: showOnRight
              };
              questionRecommendations.push(recObj);
            }
          });
        } else {
          // skip if this tag should be hidden due to a multi-recommendation
          if (hideSingleTags.has(value)) {
            return;
          }
          const answerNode = allQuestions.value.find(q => q.key === value);
          let recommendation = null;
          let recommendationSection = null;
          let references = null;
          let image = null;
          let imageCaption = null;
          let showOnRight = false;

          if (answerNode && answerNode.recommendation) {
            // Use answer-level recommendation if available
            recommendation = answerNode.recommendation;
            recommendationSection = answerNode.recommendationSection || questionLevelRecommendations.value[key]?.recommendationSection;
            references = answerNode.references;
            image = answerNode.image;
            imageCaption = answerNode.imageCaption;
            showOnRight = answerNode.showOnRight;
          } else if (questionLevelRecommendations.value[key]) {
            // Fall back to question-level recommendation
            recommendation = questionLevelRecommendations.value[key].recommendation;
            recommendationSection = questionLevelRecommendations.value[key].recommendationSection;
          } else {
          }

          if (recommendation) {
            const recObj = {
              question: questionData.shortTitle,
              recommendation: recommendation,
              recommendationSection: recommendationSection,
              references: references,
              answerKey: key,
              questionName: question.name,
              selectedValue: value,
              image: image,
              imageCaption: imageCaption,
              showOnRight: showOnRight
            };
            questionRecommendations.push(recObj);
          }
        }
      }
    });

    // Combine regular and multi recommendations, ordered by question appearance
    const combinedRecommendations = [...questionRecommendations];
    
    // Add multi recommendations after the last prerequisite recommendation they're related to
    multiRecsToShow.forEach(multiRec => {
      if (Array.isArray(multiRec.tags)) {
        const recTagKeys = multiRec.tags.map(tag => extractKeyFromPath(tag));
        
        // Find all prerequisite recommendations that match the multi-recommendation tags
        const matchingPrerequisiteRecs = questionRecommendations.filter(rec => 
          recTagKeys.includes(rec.selectedValue)
        );
        
        // Find the position in combinedRecommendations to insert this multi recommendation
        let insertIndex = combinedRecommendations.length;
        
        if (matchingPrerequisiteRecs.length > 0) {
          // Find the last matching prerequisite recommendation in the combined list
          let lastMatchingIndex = -1;
          for (let i = 0; i < combinedRecommendations.length; i++) {
            const rec = combinedRecommendations[i];
            if (recTagKeys.includes(rec.selectedValue)) {
              lastMatchingIndex = i;
            }
          }
          
          if (lastMatchingIndex >= 0) {
            insertIndex = lastMatchingIndex + 1;
          }
        }
        
        combinedRecommendations.splice(insertIndex, 0, {
          question: 'Multi-recommendation',
          recommendation: multiRec.recommendation,
          recommendationSection: multiRec.recommendationSection,
          references: multiRec.references || [],
          answerKey: 'multi',
          questionName: 'multi',
          selectedValue: 'multi',
          isMultiRecommendation: true,
          image: multiRec.image,
          imageCaption: multiRec.imageCaption,
          showOnRight: multiRec.showOnRight
        });
      }
    });

    survey.value.surveyResults = surveyResults;
    survey.value.questionRecommendations = combinedRecommendations;

    let allRefs: any[] = [];
    if (references.value && references.value.length > 0) {
      allAnsweredKeys.forEach(tag => {
        references.value.forEach(refObj => {
          if (refObj.tags && refObj.tags.length > 0 && refObj.reference) {
            const refTagKeys = refObj.tags.map(refTag => extractKeyFromPath(refTag));
            if (refTagKeys.includes(tag)) {
              allRefs.push(refObj.reference);
            }
          }
        });
      });
    }
    allRefs = Array.from(new Set(allRefs));
    // sort alphabetically by first author, tiebreak by year in (YYYY)
    allRefs.sort((a, b) => {
      const getAuthor = ref => {
        const match = ref.match(/^\s*([^\s,]+),/);
        return match ? match[1].trim().toLowerCase() : '';
      };
      const getYear = ref => {
        const match = ref.match(/\((\d{4})\)/);
        return match ? parseInt(match[1], 10) : 0;
      };
      const authorA = getAuthor(a);
      const authorB = getAuthor(b);
      if (authorA < authorB) return -1;
      if (authorA > authorB) return 1;
      const yearA = getYear(a);
      const yearB = getYear(b);
      return yearA - yearB;
    });
    survey.value.references = allRefs;
  });
};

const initializeSurvey = async () => {
  surveyJson.value = await loadSurveyFromApi();
  multiRecommendations.value = await loadMultiRecommendations();
  const questionsData = await loadQuestionsFromDelivery();
  allQuestions.value = questionsData.allAnswers;
  questionLevelRecommendations.value = questionsData.questionRecommendations;
  references.value = await loadReferences();
  survey.value = new Model(surveyJson.value);
  
  surveyJson.value.pages.forEach(page => {
    page.elements.forEach(element => {
      if (element.exclusiveChoices && element.exclusiveChoices.length > 0) {
        const question = survey.value.getQuestionByName(element.name);
        if (question) {
          question.exclusiveChoices = element.exclusiveChoices;
        }
      }
    });
  });
  
  initializeSurveyModel(survey.value);
};

const goBackToLastPage = () => {
  if (survey.value && survey.value.pages && survey.value.pages.length > 0) {
    survey.value.clear(false);
    survey.value.currentPageNo = survey.value.pages.length - 1;
  }
};

const getImageUrl = (imagePath) => {
  return `${window.location.origin}/.imaging/default/dam${imagePath}/jcr:content.jpg`;
};

const groupedRecommendations = computed(() => {
  if (!survey.value?.questionRecommendations) return {};
  
  const groups = {};
  survey.value.questionRecommendations.forEach(rec => {
    const section = rec.recommendationSection || 'General Recommendations';
    if (!groups[section]) {
      groups[section] = [];
    }
    groups[section].push(rec);
  });
  
  return groups;
});

onMounted(async () => {
  await initializeSurvey();
});
</script>

<template>
  <div class="survey-container">
    <SurveyComponent v-if="survey" :model="survey" />
    <div class="thank-you-box" v-if="survey && survey.state === 'completed'">
      <button @click="goBackToLastPage" class="back-button">Back to Survey</button>
      <h2>Remote Camera Decision Support Tool - Recommendation Report</h2>
      <div class="version-info">
        <p>Version: v0.1 (Demo)</p>
        <p>Date/time obtained: {{ new Date().toLocaleString() }}</p>
      </div>
      <div class="note-section">
        <p><strong>Note:</strong> The Remote Camera Decision Support Tool is meant to serve as a supplementary tool while designing your camera program. The recommendations provided are not a replacement for investigation into the optimal study design for your remote camera program. Instead, they are meant to serve as a starting point grounded in western science best practices. Additional research is recommended.</p>
      </div>
      <div v-if="survey.questionRecommendations && survey.questionRecommendations.length > 0">
        <h4>Recommendations</h4>
        <div v-for="(recommendations, sectionName) in groupedRecommendations" :key="sectionName" class="recommendation-section">
          <h5 class="section-header">{{ sectionName }}</h5>
          <table class="results-table">
            <tbody>
              <tr v-for="(rec, index) in recommendations" :key="index">
                <td>
                  <div v-if="rec.image && rec.showOnRight" class="recommendation-with-right-image">
                    <div class="recommendation-content">
                      <div v-html="rec.recommendation"></div>
                    </div>
                    <div class="recommendation-image-right">
                      <img :src="getImageUrl(rec.image)" :alt="'Image for ' + rec.question" class="recommendation-img" @error="console.log('Image failed to load:', rec.image)" />
                      <div v-if="rec.imageCaption" class="image-caption">{{ rec.imageCaption }}</div>
                    </div>
                  </div>
                  <div v-else-if="rec.image" class="recommendation-with-full-image">
                    <div v-html="rec.recommendation"></div>
                    <div class="recommendation-image-full">
                      <img :src="getImageUrl(rec.image)" :alt="'Image for ' + rec.question" class="recommendation-img" @error="console.log('Image failed to load:', rec.image)" />
                      <div v-if="rec.imageCaption" class="image-caption">{{ rec.imageCaption }}</div>
                    </div>
                  </div>
                  <div v-else class="recommendation-text-only">
                    <div v-html="rec.recommendation"></div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-if="survey.references && survey.references.length > 0">
        <h4>References</h4>
        <div>
          <ul>
            <li v-for="(ref, idx) in survey.references" :key="idx" v-html="ref"></li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
:root {
  height: 100%;
}
body {
  margin: 0;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f0f0f0;
}
.survey-container {
  width: 1250px;
  padding: 30px;
  background-color: #F6F6F6;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.thank-you-box {
  margin-top: 20px;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #F6F6F6;
  color: black;
}
.version-info {
  margin: 20px 0;
  color: #666;
}
.note-section {
  margin: 20px 0;
  padding: 15px;
  background-color: #f9f9f9;
  border-left: 4px solid #666;
}
.results-table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}
.results-table th,
.results-table td {
  border: 1px solid #ddd;
  padding: 12px;
  text-align: left;
}
.results-table th {
  background-color: #f5f5f5;
  font-weight: bold;
}
.results-table tr:nth-child(even) {
  background-color: #fafafa;
}
.results-table tr:hover {
  background-color: #f0f0f0;
}
#custom-tooltip {
  position: fixed;
  display: none;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px 12px;
  max-width: 300px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 1000;
  font-size: 14px;
  line-height: 1.4;
}
#custom-tooltip p {
  margin: 0 0 8px 0;
}
#custom-tooltip p:last-child {
  margin-bottom: 0;
}
#custom-tooltip strong {
  font-weight: 600;
}
#custom-tooltip em {
  font-style: italic;
}
#custom-tooltip ul, #custom-tooltip ol {
  margin: 4px 0;
  padding-left: 20px;
}
#custom-tooltip li {
  margin: 2px 0;
}
.back-button {
  margin: 20px 0;
  padding: 10px 24px;
  background-color: #eee;
  border: 1px solid #888;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
}
.back-button:hover {
  background-color: #e0e0e0;
}
.hover-tip {
  margin-top: 10px;
  color: #1976d2;
  font-size: 14px;
  font-style: italic;
}
.tooltip-icon {
  cursor: help;
  user-select: none;
  display: inline-block;
  vertical-align: middle;
  margin-right: 6px;
}
.sd-item__control-label {
  display: inline-flex !important;
  align-items: center;
  gap: 4px;
}
.recommendation-with-right-image {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}
.recommendation-content {
  flex: 1;
}
.recommendation-image-right {
  flex: 0 0 300px;
  max-width: 300px;
}
.recommendation-with-full-image {
  display: block;
}
.recommendation-image-full {
  margin-top: 15px;
  text-align: center;
}
.recommendation-text-only {
  display: block;
}
.recommendation-img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.recommendation-image-right .recommendation-img {
  max-width: 100%;
  max-height: 300px;
  object-fit: cover;
}
.recommendation-image-full .recommendation-img {
  max-width: 100%;
  max-height: 500px;
  object-fit: contain;
}
.image-caption {
  font-style: italic;
  font-size: 14px;
  color: #666;
  text-align: center;
  margin-top: 8px;
  line-height: 1.4;
}
</style>